import React from 'react';
import { clsx } from 'clsx';

/**
 * Skeleton loader pro zobrazeni nacitaciho stavu
 * Pouziti:
 * - <Skeleton /> - zakladni obdelnik
 * - <Skeleton variant="circle" /> - kruh (avatar)
 * - <Skeleton variant="text" /> - radek textu
 * - <Skeleton.Card /> - karta
 * - <Skeleton.Table rows={5} /> - tabulka
 */

interface SkeletonProps {
  variant?: 'rectangular' | 'circular' | 'text';
  width?: string | number;
  height?: string | number;
  className?: string;
  animation?: 'pulse' | 'wave' | 'none';
  rounded?: boolean | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
}

const Skeleton: React.FC<SkeletonProps> & {
  Card: typeof SkeletonCard;
  Table: typeof SkeletonTable;
  List: typeof SkeletonList;
  Dashboard: typeof SkeletonDashboard;
  Chart: typeof SkeletonChart;
  Form: typeof SkeletonForm;
} = ({
  variant = 'rectangular',
  width,
  height,
  className,
  animation = 'pulse',
  rounded = 'md',
}) => {
  const baseClasses = 'text-gray-200 dark:text-zinc-700';
  const resolvedWidth = width ?? (variant === 'circular' ? height : undefined);
  const sizingClasses = width == null && variant !== 'circular' ? 'block w-full' : 'block';

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-zinc-700 dark:via-zinc-600 dark:to-zinc-700 bg-[length:200%_100%]',
    none: '',
  };

  const roundedClasses = {
    true: 'rounded',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    full: 'rounded-full',
  };

  const variantClasses = {
    rectangular: '',
    circular: '',
    text: 'rounded h-4',
  };

  return (
    <svg
      className={clsx(
        baseClasses,
        sizingClasses,
        animationClasses[animation],
        variant !== 'circular' && roundedClasses[rounded as keyof typeof roundedClasses],
        variantClasses[variant],
        className,
      )}
      width={resolvedWidth}
      height={height}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      role="presentation"
    >
      {variant === 'circular' ? (
        <circle cx="50" cy="50" r="50" className="fill-current" />
      ) : (
        <rect x="0" y="0" width="100" height="100" rx="12" ry="12" className="fill-current" />
      )}
    </svg>
  );
};

// Skeleton Card - pro kartove layouty
interface SkeletonCardProps {
  showImage?: boolean;
  showTitle?: boolean;
  showDescription?: boolean;
  showFooter?: boolean;
  className?: string;
}

const SkeletonCard: React.FC<SkeletonCardProps> = ({
  showImage = true,
  showTitle = true,
  showDescription = true,
  showFooter = false,
  className,
}) => (
  <div
    className={clsx(
      'bg-white dark:bg-zinc-800 rounded-2xl p-4 shadow-lg border border-gray-100 dark:border-zinc-700',
      className,
    )}
  >
    {showImage && <Skeleton height={120} className="mb-4" rounded="xl" />}
    {showTitle && <Skeleton variant="text" width="60%" height={24} className="mb-2" />}
    {showDescription && (
      <>
        <Skeleton variant="text" width="100%" className="mb-1" />
        <Skeleton variant="text" width="80%" className="mb-1" />
        <Skeleton variant="text" width="40%" />
      </>
    )}
    {showFooter && (
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-zinc-700 flex justify-between">
        <Skeleton width={80} height={32} rounded="lg" />
        <Skeleton width={80} height={32} rounded="lg" />
      </div>
    )}
  </div>
);

// Skeleton Table - pro tabulky
interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}

const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 4,
  showHeader = true,
  className,
}) => (
  <div className={clsx('overflow-hidden rounded-lg', className)}>
    {showHeader && (
      <div className="bg-gray-100 dark:bg-zinc-800 p-4 flex gap-4">
        {[...Array(columns)].map((_, i) => (
          <Skeleton key={i} variant="text" width={`${100 / columns}%`} height={20} />
        ))}
      </div>
    )}
    <div className="divide-y divide-gray-100 dark:divide-zinc-700">
      {[...Array(rows)].map((_, rowIndex) => (
        <div key={rowIndex} className="p-4 flex gap-4 items-center">
          {[...Array(columns)].map((_, colIndex) => (
            <Skeleton key={colIndex} variant="text" width={`${100 / columns}%`} height={16} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

// Skeleton List - pro seznamy
interface SkeletonListProps {
  items?: number;
  showAvatar?: boolean;
  showSecondary?: boolean;
  className?: string;
}

const SkeletonList: React.FC<SkeletonListProps> = ({
  items = 5,
  showAvatar = true,
  showSecondary = true,
  className,
}) => (
  <div className={clsx('space-y-4', className)}>
    {[...Array(items)].map((_, i) => (
      <div key={i} className="flex items-center gap-4">
        {showAvatar && <Skeleton variant="circular" width={48} height={48} />}
        <div className="flex-1">
          <Skeleton variant="text" width="40%" height={18} className="mb-2" />
          {showSecondary && <Skeleton variant="text" width="70%" height={14} />}
        </div>
      </div>
    ))}
  </div>
);

// Skeleton Dashboard - pro dashboard karty
interface SkeletonDashboardProps {
  cards?: number;
  columns?: 2 | 3 | 4;
  className?: string;
}

const SkeletonDashboard: React.FC<SkeletonDashboardProps> = ({
  cards = 4,
  columns = 4,
  className,
}) => {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={clsx('grid gap-4', gridCols[columns], className)}>
      {[...Array(cards)].map((_, i) => (
        <div
          key={i}
          className="bg-white dark:bg-zinc-800 rounded-2xl p-5 shadow-lg border border-gray-100 dark:border-zinc-700"
        >
          <div className="flex items-center justify-between mb-4">
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton width={60} height={20} rounded="lg" />
          </div>
          <Skeleton variant="text" width="30%" height={14} className="mb-2" />
          <Skeleton variant="text" width="60%" height={32} />
        </div>
      ))}
    </div>
  );
};

// Skeleton Chart - pro grafy
interface SkeletonChartProps {
  type?: 'line' | 'bar' | 'pie';
  height?: number;
  className?: string;
}

const SkeletonChart: React.FC<SkeletonChartProps> = ({
  type: _type = 'line',
  height = 300,
  className,
}) => (
  <div
    className={clsx(
      'bg-white dark:bg-zinc-800 rounded-2xl p-4 shadow-lg border border-gray-100 dark:border-zinc-700',
      className,
    )}
  >
    <div className="flex justify-between items-center mb-4">
      <Skeleton variant="text" width={150} height={24} />
      <div className="flex gap-2">
        <Skeleton width={60} height={28} rounded="lg" />
        <Skeleton width={60} height={28} rounded="lg" />
      </div>
    </div>
    <Skeleton height={height} rounded="lg" />
  </div>
);

// Skeleton Form - pro formulare
interface SkeletonFormProps {
  fields?: number;
  showSubmit?: boolean;
  className?: string;
}

const SkeletonForm: React.FC<SkeletonFormProps> = ({
  fields = 4,
  showSubmit = true,
  className,
}) => (
  <div className={clsx('space-y-6', className)}>
    {[...Array(fields)].map((_, i) => (
      <div key={i}>
        <Skeleton variant="text" width={100} height={16} className="mb-2" />
        <Skeleton height={40} rounded="lg" />
      </div>
    ))}
    {showSubmit && (
      <div className="flex gap-4 pt-4">
        <Skeleton width={120} height={44} rounded="lg" />
        <Skeleton width={100} height={44} rounded="lg" />
      </div>
    )}
  </div>
);

// Pripojeni sub-komponent
Skeleton.Card = SkeletonCard;
Skeleton.Table = SkeletonTable;
Skeleton.List = SkeletonList;
Skeleton.Dashboard = SkeletonDashboard;
Skeleton.Chart = SkeletonChart;
Skeleton.Form = SkeletonForm;

export {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonList,
  SkeletonDashboard,
  SkeletonChart,
  SkeletonForm,
};

export default Skeleton;
